// =====================================================
// PropertyDetails.jsx  (v3)
// Estate Experts — Property Details Page
// Prefix  : propdetail-
// Sections: breadcrumb · gallery · overview ·
//           property info table · specifications ·
//           amenities · property details strip ·
//           map · description · main features ·
//           booking sidebar (right, untouched logic)
// =====================================================

import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../../../../assets/css/Buyer/Properties/propertydetails.css';
import MyNavbar from 'components/Buyer/navbar/MyNavbar';
import Footer from 'components/Buyer/footer/Footer';
import HeroSection from 'subscreens/general/pageheros/HeroSection';
import ExBookVisitRequest from 'components/Buyer/visitRequest/ExBookVisitRequest';
import axios from 'axios';
import AppContext from 'context/AppContext';
import Swal from 'sweetalert2';
import { Heart, Share2, ChevronLeft, MapPin, Clock, Info } from 'lucide-react';
import {
  FaBed, FaBath, FaRulerCombined, FaCar,
  FaBuilding, FaLayerGroup, FaHashtag, FaMap
} from 'react-icons/fa';
import {
  FiCheckCircle, FiZap, FiWifi, FiDroplet,
  FiSun, FiHome
} from 'react-icons/fi';

// ── Time slots ─────────────────────────────────────
const TIME_SLOTS = [
  '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM',
  '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM',
];

// ── Amenity config (boolean fields → label+icon) ───
const AMENITY_MAP = [
  { key: 'electricity', label: 'Electricity', Icon: FiZap },
  { key: 'gas', label: 'Gas', Icon: FiSun },
  { key: 'internet', label: 'Internet', Icon: FiWifi },
  { key: 'water_source', label: 'Water Supply', Icon: FiDroplet },
  { key: 'boundary_wall', label: 'Boundary Wall', Icon: FiCheckCircle },
  { key: 'corner_plot', label: 'Corner Plot', Icon: FiCheckCircle },
  { key: 'garden_available', label: 'Garden', Icon: FiCheckCircle },
  { key: 'balcony', label: 'Balcony', Icon: FiCheckCircle },
  { key: 'servant_quarters', label: 'Servant Quarter', Icon: FiCheckCircle },
  { key: 'swimming_pool', label: 'Swimming Pool', Icon: FiCheckCircle },
  { key: 'lift_available', label: 'Lift Available', Icon: FiCheckCircle },
];

// ── Feature icons for the bottom cards ────────────
const FEATURE_ICONS = ['🛡️', '🌳', '🛣️', '🏫', '🛒', '💧', '⚡', '🚗'];

// ══════════════════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════════════════
const PropertyDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { baseUrl, baseUrlForFiles, loggedInBuyer, authToken } = useContext(AppContext);

  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [bookingDate, setBookingDate] = useState('');

  // ── Fetch ────────────────────────────────────────
  useEffect(() => {
    const formData = new FormData();
    formData.append('id', id);
    axios.post(`${baseUrl}/buyer/property/fetchSingleProperty`, formData, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then(res => { setProperty(res.data.property); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, [id]);

  // ── Wishlist ────────────────────────────────────
  const addToWishlist = () => {
    const formData = new FormData();
    formData.append('exbuyer_id', loggedInBuyer?.id || 1);
    formData.append('exproperty_id', property?.id);
    axios.post(`${baseUrl}/buyer/wishlist/addToWishlist`, formData)
      .then((res) => {
        console.log(res.data)
        setIsFavorite(true);
        Swal.fire({ icon: 'success', title: 'Added to Wishlist ❤️', timer: 2000, timerProgressBar: true, showConfirmButton: false });
      })
      .catch(err => console.error(err));
  };
  const handleChatWithSeller = () => {
  if (!loggedInBuyer) {
    Swal.fire({
      icon: 'warning',
      title: 'Login Required',
      text: 'Please login first to chat with the seller.',
      confirmButtonText: 'Login',
    }).then(() => {
      navigate('/buyer/login');
    });

    return;
  }

  const formData = new FormData();

  formData.append('exseller_id', property.exseller_id);
  formData.append('exproperty_id', property.id);

  axios.post(
    `${baseUrl}/buyer/conversation/createConversation`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  )
    .then((res) => {
      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Chat Started',
          text: 'You can now chat with the seller.',
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          navigate(`/buyer/chats`);
        });
      }
    })
    .catch((error) => {
      console.error('Chat creation error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Unable to Start Chat',
        text: error.response?.data?.message || 'Something went wrong. Please try again.',
      });
    });
};

  // ── Share ───────────────────────────────────────
  const handleSocialShare = (platform) => {
    const url = window.location.href;
    const title = property?.property_title || 'Property';
    const map = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    };
    if (map[platform]) window.open(map[platform], '_blank');
    setShowShare(false);
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => { Swal.fire({ icon: 'success', title: 'Link copied!', timer: 1500, showConfirmButton: false }); setShowShare(false); });
  };

  // ── Loading / Error ──────────────────────────────
  if (loading) return (
    <>
      <MyNavbar />
      <div className="propdetail-loading">
        <div className="propdetail-spinner" />
        <span>Loading property details…</span>
      </div>
      <Footer />
    </>
  );

  if (!property) return (
    <>
      <MyNavbar />
      <div className="propdetail-error">
        <FiHome size={48} style={{ opacity: .25 }} />
        <h2>Property Not Found</h2>
        <button className="propdetail-error-btn" onClick={() => navigate('/properties')}>Back to Properties</button>
      </div>
      <Footer />
    </>
  );

  // ── Images array ─────────────────────────────────
  const images = property.expropertyimages?.map(img => `${baseUrlForFiles}${img.images}`) || [];

  // ── Amenities present ────────────────────────────
  const presentAmenities = AMENITY_MAP.filter(a =>
    property[a.key] === 1 || property[a.key] === true || property[a.key] === '1'
  );

  // ── Features from comma string ───────────────────
  const featuresList = property.features
    ? property.features.split(',').map(f => f.trim()).filter(Boolean)
    : [];

  // ── Map embed ────────────────────────────────────
  const mapSrc = property.latitude && property.longitude
    ? `https://maps.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`
    : `https://maps.google.com/maps?q=${encodeURIComponent(property.full_address || property.location || 'Pakistan')}&z=14&output=embed`;

  // ══════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════
  return (
    <>
      <MyNavbar />

      <HeroSection
        title="Property Details"
        subtitle="Expert professionals at your service"
        image="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1920"
      />

      <div className="propdetail-page">

        {/* ── Breadcrumb ── */}
        <div className="propdetail-breadcrumb">
          <button className="propdetail-back-btn" onClick={() => navigate('/properties')}>
            <ChevronLeft size={15} /> Back to Properties
          </button>
          {(property.location || property.city) && (
            <div className="propdetail-location-row">
              <MapPin size={13} />
              <span>{[property.location].filter(Boolean).join(', ')}</span>
            </div>
          )}
        </div>

        {/* ── Layout ── */}
        <div className="propdetail-layout">

          {/* ══════════════════════════════════════
              LEFT — main content
          ══════════════════════════════════════ */}
          <div className="propdetail-main">

            {/* 1. Gallery */}
            <div className="propdetail-gallery">
              <div className="propdetail-main-img-wrap">
                <img
                  src={images[selectedImage] || 'https://via.placeholder.com/800x420?text=No+Image'}
                  alt={property.property_title}
                  className="propdetail-main-img"
                />

                {/* Fav + Share */}
                <div className="propdetail-img-actions">
                  <button className="propdetail-img-btn" onClick={addToWishlist} title="Add to wishlist">
                    <Heart size={18} fill={isFavorite ? '#dc3545' : 'none'} color={isFavorite ? '#dc3545' : '#000'} />
                  </button>
                  <button className="propdetail-img-btn" onClick={() => setShowShare(true)} title="Share">
                    <Share2 size={17} color="#000" />
                  </button>
                </div>

                {images.length > 1 && (
                  <div className="propdetail-img-counter">{selectedImage + 1}/{images.length}</div>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="propdetail-thumbs">
                  {images.map((src, i) => (
                    <button
                      key={i}
                      className={`propdetail-thumb ${selectedImage === i ? 'active' : ''}`}
                      onClick={() => setSelectedImage(i)}
                    >
                      <img src={src} alt={`thumb-${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Overview */}
            <section>
              <h3 className="propdetail-section-title">Overview</h3>
              <div className="propdetail-overview-grid">
                {[
                  { icon: <FaBuilding size={17} />, lbl: 'Property Type', val: property.property_type },
                  { icon: <FiHome size={17} />, lbl: 'Developer', val: property.developer || 'Estate Experts' },
                  { icon: <FaRulerCombined size={15} />, lbl: 'Size', val: property.size },
                  { icon: <FiCheckCircle size={16} />, lbl: 'Listing Type', val: property.listingType },
                ].map(({ icon, lbl, val }) => val && (
                  <div className="propdetail-overview-tile" key={lbl}>
                    <div className="propdetail-overview-icon">{icon}</div>
                    <span className="propdetail-overview-lbl">{lbl}</span>
                    <span className="propdetail-overview-val">{val}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* 3. Property Information table */}
            <section>
              <h3 className="propdetail-section-title">Property Information</h3>
              <div className="propdetail-info-table">
                {[
                  [
                    { lbl: 'Property ID', val: property.id ? `P-${String(property.id).padStart(5, '0')}` : '—' },
                    { lbl: 'Land Area', val: property.land_area || '—' },
                    { lbl: 'Size', val: property.size || '—' },
                  ],
                  [
                    { lbl: 'Property Type', val: property.property_type || '—' },
                    { lbl: 'Property Stage', val: property.property_stage || '—' },
                    { lbl: 'Furnished', val: property.furnished || '—' },
                  ],
                  [
                    { lbl: 'Listing Type', val: property.listingType || '—' },
                    { lbl: 'Year Built', val: property.year_built || '—' },
                    { lbl: 'Status', val: property.status, isStatus: true },
                  ],
                  [
                    { lbl: 'City', val: property.city || '—' },
                    { lbl: 'Location', val: property.location || '—' },
                    { lbl: 'Water Source', val: property.water_source || '—' },
                  ],
                  [
                    { lbl: 'Full Address', val: property.full_address || '—' },
                    { lbl: 'Plot Number', val: property.plot_number || '—' },
                    { lbl: '', val: '' },
                  ],
                ].map((row, ri) => (
                  <div className="propdetail-info-row" key={ri}>
                    {row.map(({ lbl, val, isStatus }) => (
                      <div className="propdetail-info-cell" key={lbl}>
                        {lbl && <div className="propdetail-info-lbl">{lbl}</div>}
                        {isStatus && val ? (
                          <span className="propdetail-status-badge">{val}</span>
                        ) : (
                          <div className="propdetail-info-val">{val}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>

            {/* 4. Specifications */}
            <section>
              <h3 className="propdetail-section-title">Property Specifications</h3>
              <div className="propdetail-specs-grid">
                {[
                  { Icon: FaBed, lbl: 'Bedrooms', val: property.bedrooms },
                  { Icon: FaBath, lbl: 'Bathrooms', val: property.bathrooms },
                  { Icon: FaBath, lbl: 'Washrooms', val: property.washrooms },
                  { Icon: FaCar, lbl: 'Parking Spaces', val: property.parking_spaces },
                  { Icon: FaBuilding, lbl: 'Floors', val: property.floors },
                  { Icon: FaLayerGroup, lbl: 'Floor Number', val: property.floor_number },
                  { Icon: FaRulerCombined, lbl: 'Land Area', val: property.land_area },
                  { Icon: FaHashtag, lbl: 'Plot Number', val: property.plot_number },
                ].filter(s => s.val != null && s.val !== '').map(({ Icon, lbl, val }) => (
                  <div className="propdetail-spec-card" key={lbl}>
                    <Icon className="propdetail-spec-icon" size={18} />
                    <div>
                      <span className="propdetail-spec-lbl">{lbl}</span>
                      <span className="propdetail-spec-val">{val}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 5. Amenities & Features */}
            {presentAmenities.length > 0 && (
              <section>
                <h3 className="propdetail-section-title">Amenities & Features</h3>
                <div className="propdetail-amenities-wrap">
                  {presentAmenities.map(({ label, Icon }) => (
                    <span className="propdetail-amenity-chip" key={label}>
                      <Icon size={13} /> {label}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* 6. Property Details strip */}
            <section>
              <h3 className="propdetail-section-title">Property Details</h3>
              <div className="propdetail-details-strip">
                {[
                  { icon: '🪑', lbl: 'Furnished', val: property.furnished },
                  { icon: '🏗️', lbl: 'Property Stage', val: property.property_stage },
                  { icon: '📅', lbl: 'Year Built', val: property.year_built },
                  { icon: '💧', lbl: 'Water Source', val: property.water_source },
                  { icon: '📋', lbl: 'Property Status', val: property.status, green: true },
                ].filter(d => d.val).map(({ icon, lbl, val, green }) => (
                  <div className="propdetail-detail-tile" key={lbl}>
                    <span className="propdetail-detail-tile-icon">{icon}</span>
                    <div>
                      <span className="propdetail-detail-tile-lbl">{lbl}</span>
                      <span className={`propdetail-detail-tile-val ${green ? 'propdetail-detail-tile-val--green' : ''}`}>{val}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 7. Map */}
            <section>
              <h3 className="propdetail-section-title">Location</h3>
              <div className="propdetail-map-wrap">
                <iframe
                  src={mapSrc}
                  title="Property Location"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              {property.full_address && (
                <div className="propdetail-map-address">
                  <MapPin size={13} />
                  <span>{property.full_address}{property.city ? `, ${property.city}` : ''}{', Pakistan'}</span>
                </div>
              )}
            </section>

            {/* 8. Description */}
            {property.description && (
              <section>
                <h3 className="propdetail-section-title">Description</h3>
                <p className="propdetail-desc">{property.description}</p>
              </section>
            )}

            {/* 9. Main Features */}
            {featuresList.length > 0 && (
              <section>
                <h3 className="propdetail-section-title">Main Features</h3>
                <div className="propdetail-features-grid">
                  {featuresList.map((f, i) => (
                    <div className="propdetail-feature-card" key={i}>
                      <span className="propdetail-feature-icon">
                        {FEATURE_ICONS[i % FEATURE_ICONS.length]}
                      </span>
                      <p className="propdetail-feature-title">{f}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

          </div>{/* /propdetail-main */}

          {/* ══════════════════════════════════════
              RIGHT — Booking Sidebar (unchanged logic)
          ══════════════════════════════════════ */}
          <div className="propdetail-sidebar">
            <div className="propdetail-booking-card">

              {/* Price */}
              <div className="propdetail-price-section">
                <span className="propdetail-price-lbl">Price</span>
                <h3 className="propdetail-price-val">{property.price}</h3>
              </div>

              {/* The existing visit-request component stays here */}
              <ExBookVisitRequest
                property_id={property.id}
                seller_id={property.exseller_id}
                buyer_id={loggedInBuyer?.id || property.exbuyer_id}
              />

              {/* Chat with Seller */}
              <button
                className="propdetail-chat-btn"
                onClick={handleChatWithSeller}
              >
                💬 Chat with Seller
              </button>

              <p className="propdetail-disclaimer">
                By submitting this form you agree to Terms of Use.
              </p>
            </div>
          </div>

        </div>{/* /propdetail-layout */}

      </div>{/* /propdetail-page */}

      {/* ── Share Popup ── */}
      {showShare && (
        <div className="propdetail-share-overlay" onClick={() => setShowShare(false)}>
          <div className="propdetail-share-box" onClick={e => e.stopPropagation()}>
            <button className="propdetail-share-close" onClick={() => setShowShare(false)}>×</button>
            <h3 className="propdetail-share-title">Share Property</h3>
            <div className="propdetail-share-grid">
              {[
                { cls: 'whatsapp', icon: '💬', label: 'WhatsApp', fn: () => handleSocialShare('whatsapp') },
                { cls: 'facebook', icon: '📘', label: 'Facebook', fn: () => handleSocialShare('facebook') },
                { cls: 'twitter', icon: '🐦', label: 'Twitter', fn: () => handleSocialShare('twitter') },
                { cls: 'copylink', icon: '🔗', label: 'Copy Link', fn: handleCopyLink },
              ].map(({ cls, icon, label, fn }) => (
                <button key={cls} className={`propdetail-share-opt ${cls}`} onClick={fn}>
                  <span className="propdetail-share-icon">{icon}</span>
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default PropertyDetails;